<template>
    <analytics-stat-card
        :loading="loading"
        :error="Boolean(error)"
        :percentage="true"
        :difference="dataComputed.difference"
        :value="dataComputed.number?.value"
        :color="dataComputed.difference < 0 ? 'bad' : 'good'"
        :direction="dataComputed.difference < 0 ? 'down' : 'up'"
    >
        <template v-slot:title>
            <slot name="title">Unique Click-Through Rate</slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <span>{{ dataComputed.number?.label || '' }}</span>

        <template v-slot:explainHeader>Unique Click-Through Rate</template>
        <template v-slot:explainContent>
            <p>
                <strong>Messages Unique Click-Through Rate</strong><br />
                In the Context of Messages sent, in particular the ones that provide a clickable link, this metric
                allows you to evaluate success through the unique clicks an Initiative receives relative to the quantity
                of messages delivered.
            </p>
            <p>
                <strong>Message Unique Click-Through Rate %</strong><br />
                Variation of Unique Click-Through Rate relative to the previous period.
            </p>
        </template>
    </analytics-stat-card>
</template>

<script setup>
import { outboundMessagesAnalyticsClient } from '~/services';
import { computed, watch } from 'vue';
import AnalyticsStatCard from '../../partial/AnalyticsStatCard.vue';
import { formatPercentage } from '~/components/filters';
import {
    computeComparisonRange,
    calculateDifference,
    metricsInputValidator,
    minUniqueClickThroughRateComparisonDate,
} from '~/utils/analytics';
import { useVersionedRequest } from '~/composables/useVersionedRequest';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    const values = response.value?.data?.values || [];

    if (!values.length) {
        return {
            number: undefined,
            difference: undefined,
        };
    }

    const number = values.length >= 2 ? values[1] : values[0];
    const difference = values.length >= 2 ? calculateDifference(values) : undefined;

    return {
        number: {
            value: number,
            label: formatPercentage(Math.abs(number)),
        },
        difference: difference,
    };
});

watch(
    [() => props.fetchParams],
    () => {
        const range = computeComparisonRange(props.fetchParams.range, Boolean(props.groupId), Boolean(props.orgUnitId));
        if (minUniqueClickThroughRateComparisonDate.isAfter(range[0])) {
            range.splice(0, 1);
        }

        const params = {
            ...props.fetchParams,
            range: range,
        };

        if (props.campaignId) {
            params.filter = {
                campaign_ids: [props.campaignId],
            };
        }

        fetchData(
            outboundMessagesAnalyticsClient.getUniqueClickThroughRate(
                {
                    teamId: Spark.state.currentTeam.id,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);

defineExpose({
    value: computed(() => dataComputed.value.number?.value),
    loading,
});
</script>
